import { AssociatedContentSelectionFragment } from 'app/graphql/__generated__/types/contento';
import ContinueWatching from 'app/utils/models/ContinueWatching';
import { VideoObject, WithContext } from 'schema-dts';

export const getSmartStartProgress = (
    continueWatchingIsActive: boolean,
    continueWatchingItems: ContinueWatching[],
    smartStart: any
): number => {
    if (!continueWatchingIsActive) {
        return null;
    }

    let progress;
    const smartStartContinueWatching = continueWatchingItems?.find(
        (item) => item.videoId === smartStart?.videoSvtId
    );

    if (smartStartContinueWatching && smartStart?.item.duration) {
        progress = Math.round((smartStartContinueWatching.position / smartStart.item.duration) * 100);
    }

    return progress;
};

export const getAssociatedContentProgress = (
    continueWatchingIsActive: boolean,
    continueWatchingItems: ContinueWatching[],
    associatedContent: AssociatedContentSelectionFragment[]
): { [svtId: string]: number } => {
    if (!continueWatchingIsActive) {
        return null;
    }

    const associatedContentProgress = associatedContent.reduce((result, section) => {
        section.items?.forEach((item) => {
            const match = continueWatchingItems.find(
                (continueWatchingItem) =>
                    'videoSvtId' in item.item && continueWatchingItem.videoId.startsWith(item.item.videoSvtId)
            );

            if (match && 'duration' in item.item) {
                const progress = Math.round((match.position / item.item.duration) * 100);

                // eslint-disable-next-line no-param-reassign
                result[item.item.svtId] = progress;
            }
        });

        return result;
    }, {});

    return associatedContentProgress;
};

export const getSubheading = (item, isTitle) => {
    const type = item.parent?.__typename;

    if (type === 'Single') {
        return item.durationFormatted;
    }

    if (isTitle) {
        return item?.shortDescription;
    }

    return `${item.name} • ${item.durationFormatted}`;
};

const getEndOffset = (highlight, highlights, currentIndex) => {
    if (currentIndex === highlights.length - 1) {
        return highlight.positionInSeconds + 60;
    }

    return highlights[currentIndex + 1].positionInSeconds - 1;
};

export const getStructuredData = (detailsPage, image, contentUrl, isLive): string => {
    const structuredData: WithContext<VideoObject> = {
        '@context': 'https://schema.org',
        '@type': 'VideoObject',
        thumbnailUrl: image,
        contentUrl: `https://svtplay.se${contentUrl}`,
        name: detailsPage.item.name,
        datePublished: detailsPage.item?.validFrom,
        uploadDate: detailsPage.item?.validFrom,
        expires: detailsPage.item?.validTo,
        description: detailsPage.description,
        duration: detailsPage.item?.durationISOFormatted,
    };

    if (detailsPage?.castAndCrew[0]?.people) {
        const structuredActors = detailsPage.castAndCrew[0].people.map((actor) => ({
            '@type': 'Person',
            name: actor.name,
        }));

        structuredData.actor = structuredActors;
    }

    if (detailsPage?.castAndCrew[1]?.people) {
        const directorStructuredData = detailsPage.castAndCrew[1].people.map((director) => ({
            '@type': 'Person',
            name: director.name,
        }));

        structuredData.director = directorStructuredData;
    }

    if (isLive || detailsPage.isUpcomingLive) {
        structuredData.publication = {
            '@type': 'BroadcastEvent',
            name: detailsPage.heading,
            isLiveBroadcast: true,
            startDate: detailsPage.item?.validFrom,
            endDate: detailsPage.item?.validTo,
        };
    }

    if (detailsPage.moreDetails.restrictions.onlyAvailableInSweden) {
        structuredData.regionsAllowed = 'SWE';
    }

    if (detailsPage.item?.highlights) {
        const highlightClips = detailsPage.item.highlights.map((highlight, index) => ({
            '@type': 'Clip',
            name: highlight.name,
            startOffset: highlight.positionInSeconds,
            endOffset: getEndOffset(highlight, detailsPage.item.highlights, index),
            url: `https://svtplay.se${contentUrl}?position=${highlight.positionInSeconds}&highlight=${highlight.slug}`,
        }));

        structuredData.hasPart = highlightClips;
    }

    return JSON.stringify(structuredData);
};
